/*
 * Cyberlytics additive generic OIDC provider (Authentik) for Cal.com.
 *
 * Env-gated: the provider is registered in next-auth-options.ts ONLY when
 * AUTH_OIDC_ISSUER, AUTH_OIDC_CLIENT_ID and AUTH_OIDC_CLIENT_SECRET are all
 * set. When the gate is closed, this module contributes nothing and no
 * existing provider, team, or EE code is affected in any way.
 *
 * Security properties (enforced here and by Cal.com's signIn callback):
 * - Authorization code flow with PKCE + state (+ nonce via the id_token).
 * - Verified-email-only linking/JIT: profile() surfaces email_verified and
 *   Cal.com's signIn callback refuses to link or provision users whose
 *   email is not verified by the IdP.
 */
import type { OAuthConfig } from "next-auth/providers";

export const OIDC_PROVIDER_ID = "oidc";

export const isOIDCLoginEnabled =
  !!process.env.AUTH_OIDC_ISSUER &&
  !!process.env.AUTH_OIDC_CLIENT_ID &&
  !!process.env.AUTH_OIDC_CLIENT_SECRET;

export const getOIDCDisplayName = () => process.env.AUTH_OIDC_NAME || "Company SSO";

interface OIDCProfile {
  sub: string;
  name?: string | null;
  preferred_username?: string | null;
  given_name?: string | null;
  family_name?: string | null;
  email?: string | null;
  email_verified?: boolean | null;
  picture?: string | null;
  groups?: string[] | null;
}

const buildOIDCProviderConfig = (): OAuthConfig<OIDCProfile> => {
  const issuer = (process.env.AUTH_OIDC_ISSUER as string).replace(/\/$/, "");
  return {
    id: OIDC_PROVIDER_ID,
    name: getOIDCDisplayName(),
    type: "oauth",
    wellKnown: `${issuer}/.well-known/openid-configuration`,
    clientId: process.env.AUTH_OIDC_CLIENT_ID as string,
    clientSecret: process.env.AUTH_OIDC_CLIENT_SECRET as string,
    authorization: { params: { scope: "openid email profile groups" } },
    checks: ["pkce", "state"],
    profile(profile) {
      const fullName = [profile.given_name, profile.family_name].filter(Boolean).join(" ");
      const name =
        profile.name || fullName || profile.preferred_username || profile.email || profile.sub;
      return {
        id: profile.sub,
        name,
        email: profile.email ?? null,
        // Surfaced so Cal.com's signIn callback can enforce verified-email-only
        // linking/JIT provisioning.
        email_verified: !!profile.email_verified,
        image: profile.picture ?? null,
        // Phase 6: surfaced so the signIn callback can map IdP groups to
        // Cal.com team memberships. Present only when the client requests
        // the "groups" scope (Authentik omits the claim otherwise).
        groups: Array.isArray(profile.groups)
          ? profile.groups.filter((g): g is string => typeof g === "string")
          : null,
      };
    },
    // Matches the existing Google/SAML providers. Cal.com's signIn callback
    // performs its own verified-email checks and account conversions before
    // the adapter runs, so the adapter's email-linking path must be allowed
    // for converted accounts to complete their first SSO login.
    allowDangerousEmailAccountLinking: true,
  };
};

// Computed once at module load; null when the env gate is closed.
export const oidcProviderConfig: OAuthConfig<OIDCProfile> | null = isOIDCLoginEnabled
  ? buildOIDCProviderConfig()
  : null;
